import React, { useContext, useEffect, useMemo } from 'react';
import * as R from 'ramda';

import { usePreferences } from '@atom/hooks/usePreferences';
import { Select } from '@atom/mui';
import {
  GrantType,
  Policy,
  PolicyAction,
  PolicyResource,
} from '@atom/types/policy';
import {
  getPolicyActionLabel,
  getResourceOptions,
  getSourceOptions,
  PolicyResourceOption,
} from '@atom/utilities/policyUtilities';

import PolicyGrantsSelection from './PolicyGrantsSelection';
import PolicyGrantTypeSelection from './PolicyGrantTypeSelection';
import PolicyModalContext from './PolicyModalContext';

import classes from './policyModals.module.css';

const { MenuItem } = Select;

interface Props {
  isEdit: boolean;
}

const renderValue = (
  value: PolicyResource,
  resourceOptions: { [key: string]: PolicyResourceOption },
) => {
  return !value ? (
    'Select'
  ) : (
    <div className={classes.selected}>
      <span style={{ marginRight: '1rem' }}>
        {resourceOptions[value]?.icon}
      </span>
      {resourceOptions[value]?.label}
    </div>
  );
};

const getActions = (
  resource: PolicyResource,
  existingPolicies: Policy[],
  grantType: GrantType,
  resourceOptions: { [key: string]: PolicyResourceOption },
  existingPolicy?: Policy,
): React.ReactNode => {
  const option = resourceOptions[resource];

  if (!option) {
    return null;
  }

  const disabledActions = new Set<PolicyAction>(
    existingPolicies
      .filter(({ grant }) => grant.type === grantType)
      .map(({ action }) => action),
  );

  // allow policy being edited to have the same action
  if (existingPolicy) {
    disabledActions.delete(existingPolicy.action);
  }

  // BUDGETING policies get operation actions options from sub source
  const actions =
    option.value !== PolicyResource.BUDGETING
      ? R.pathOr([], ['actions'], option)
      : R.pathOr([], [grantType, 'actions'], resourceOptions);

  return actions
    .map(action => ({ action, label: getPolicyActionLabel(action) }))
    .sort((first, second) => first.label.localeCompare(second.label))
    .map(({ action, label }) => (
      <MenuItem
        key={action}
        value={action}
        disabled={disabledActions.has(action)}
      >
        {disabledActions.has(action) ? (
          <div className={classes.actionRow}>
            <div>{label}</div>
            <div className={classes.actionExists}>Already Exists</div>
          </div>
        ) : (
          label
        )}
      </MenuItem>
    ));
};

const PolicyModalMain = ({ isEdit }: Props) => {
  const preferences = usePreferences();
  const { state, updateState, policy } = useContext(PolicyModalContext);

  const resourceOptions = getResourceOptions(preferences);
  const {
    resource,
    actions,
    grantType,
    existingPolicies,
    loadingExistingPolicies,
  } = state;

  const availableGrantTypes = useMemo(() => {
    return resourceOptions[resource]?.grantTypes || [];
  }, [resource]);

  const handleResourceChange = (event: any) => {
    const updatedResource = event.target.value;
    const option = resourceOptions[updatedResource];
    const updatedGrantType =
      option?.grantTypes?.length === 1 ? option.grantTypes[0] : null;

    updateState({
      resource: updatedResource,
      grantType: updatedGrantType,
      actions: [],
      grants: [],
    });
  };

  const availableActions = useMemo(() => {
    return getActions(
      resource,
      existingPolicies,
      grantType,
      resourceOptions,
      policy,
    );
  }, [resource, existingPolicies, grantType, policy]);

  useEffect(() => {
    // Auto-select action on Create if only one operation exists
    const option =
      resourceOptions[
        resource === PolicyResource.BUDGETING ? grantType : resource
      ];
    if (!isEdit && option?.actions?.length === 1) {
      const hasExisting = existingPolicies.some(
        existingPolicy => existingPolicy.action === option?.actions[0],
      );
      updateState({ actions: hasExisting ? [] : [option?.actions[0]] });
    }
  }, [availableActions, existingPolicies]);

  const sourceOptions = getSourceOptions(preferences);

  return (
    <>
      <div className={classes.inputContainer}>
        <Select
          label="Source"
          value={resource || ''}
          onChange={event => handleResourceChange(event)}
          renderValue={value => renderValue(value, resourceOptions)}
          placeholder="Select"
          loading={loadingExistingPolicies}
          displayEmpty
        >
          {sourceOptions.map(option => (
            <MenuItem
              key={resourceOptions[option].value}
              value={resourceOptions[option].value}
            >
              <span style={{ marginRight: '1rem' }}>
                {resourceOptions[option].icon}
              </span>
              {resourceOptions[option].label}
            </MenuItem>
          ))}
        </Select>
      </div>
      {!!resource && availableGrantTypes.length > 1 && (
        <div className={classes.inputContainer}>
          <PolicyGrantTypeSelection />
        </div>
      )}
      {!!grantType && <PolicyGrantsSelection />}
      {!!grantType && !!resource && (
        <div className={classes.inputContainer}>
          <Select
            multiple={!isEdit}
            label="Operations"
            value={actions || []}
            onChange={event => {
              if (isEdit) {
                updateState({ actions: [event.target.value] });
              } else {
                updateState({ actions: event.target.value });
              }
            }}
            disabled={loadingExistingPolicies}
          >
            {availableActions}
          </Select>
        </div>
      )}
    </>
  );
};

export default PolicyModalMain;
